import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Statistic, Table, Tag, Typography, Button, message } from 'antd';
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  MessageOutlined,
  ArrowRightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { buyerService } from '../../services/api';

const { Text } = Typography;

const cardStyle = { cursor: 'pointer', transition: 'box-shadow 0.2s' };
const cardHoverProps = {
  onMouseEnter: e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)',
  onMouseLeave: e => e.currentTarget.style.boxShadow = '',
};

const getColumnas = (navigate) => [
  {
    title: 'Animal',
    key: 'animal',
    render: (_, r) => r.animal?.nom_animal || `Animal #${r.id_animal}`,
  },
  {
    title: 'RP',
    key: 'rp',
    width: 110,
    render: (_, r) => r.animal?.rp_animal || '—',
  },
  {
    title: 'Raza',
    key: 'raza',
    width: 120,
    render: (_, r) => r.animal?.nom_raza || '—',
  },
  {
    title: 'Categoría',
    key: 'categoria',
    width: 130,
    render: (_, r) => r.animal?.categoria?.nom_categoria || '—',
  },
  {
    title: 'Participación',
    dataIndex: 'porcentaje_venta',
    key: 'participacion',
    width: 120,
    align: 'center',
    render: (v) => v != null ? <Tag color="orange">{v}%</Tag> : '—',
  },
  {
    title: 'Cabaña',
    key: 'cabana',
    render: (_, r) => r.animal?.cliente?.razon_social || r.animal?.cabana?.nom_cabana || '—',
  },
  {
    title: '',
    key: 'accion',
    width: 60,
    align: 'center',
    render: () => (
      <Button
        type="text"
        icon={<SearchOutlined />}
        onClick={() => navigate('/cliente/mis-animales')}
        title="Ver detalle y movimientos"
      />
    ),
  },
];

const ClienteDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    rematesActivos: 0,
    preOfertasActivas: 0,
    animalesSociedad: 0,
    ticketsAbiertos: 0,
  });
  const [animales, setAnimales] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [rematesRes, ofertasRes, animalesRes, ticketsRes] = await Promise.all([
        buyerService.getRemates(),
        buyerService.getMisOfertas(),
        buyerService.getMisAnimales(),
        buyerService.getTickets(),
      ]);

      const remates  = rematesRes.data  || [];
      const ofertas  = ofertasRes.data  || [];
      const anim     = animalesRes.data || [];
      const tickets  = ticketsRes.data  || [];

      setStats({
        rematesActivos:   remates.filter(r => r.autorizado && r.estado_evento === 1).length,
        preOfertasActivas: ofertas.length,
        animalesSociedad: anim.length,
        ticketsAbiertos:  tickets.filter(t => !['resuelto', 'cerrado'].includes(t.estado)).length,
      });

      setAnimales(anim);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Panel del Comprador</h2>

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} style={cardStyle} {...cardHoverProps}
            onClick={() => navigate('/cliente/remates')}>
            <Statistic
              title="Remates activos autorizados"
              value={stats.rematesActivos}
              prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} style={cardStyle} {...cardHoverProps}
            onClick={() => navigate('/cliente/mis-ofertas')}>
            <Statistic
              title="Mis ofertas registradas"
              value={stats.preOfertasActivas}
              prefix={<ShoppingCartOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} style={cardStyle} {...cardHoverProps}
            onClick={() => navigate('/cliente/mis-animales')}>
            <Statistic
              title="Animales en sociedad"
              value={stats.animalesSociedad}
              prefix={<TagsOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} style={cardStyle} {...cardHoverProps}
            onClick={() => navigate('/cliente/tickets')}>
            <Statistic
              title="Tickets abiertos"
              value={stats.ticketsAbiertos}
              prefix={<MessageOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Animales en sociedad */}
      <Card
        loading={loading}
        title={
          <span>
            <TagsOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
            Mis animales en sociedad
          </span>
        }
        extra={
          <a onClick={() => navigate('/cliente/mis-animales')} style={{ fontSize: 13 }}>
            Ver todos <ArrowRightOutlined />
          </a>
        }
      >
        {animales.length === 0 ? (
          <Text type="secondary">No tenés animales en sociedad registrados.</Text>
        ) : (
          <Table
            rowKey="id_venta"
            dataSource={animales}
            columns={getColumnas(navigate)}
            pagination={false}
            size="small"
            scroll={{ x: 600 }}
          />
        )}
      </Card>
    </div>
  );
};

export default ClienteDashboard;
